import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Swal from "sweetalert2";

import { api } from "../lib/api";
import { formatPhone } from "../lib/validation";
import { useFormStore } from "../store";

// Routes that should render WITHOUT the top utility bar (Help / Sign Out
// have no meaning for a visitor who hasn't authenticated yet).
const NO_TOP_NAV_PATHS = new Set(["/sign-in"]);

interface WhoamiResponse {
  supplier_id: string | null;
  is_new_supplier: boolean;
  is_authenticated: boolean;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  display_name: string;
}

/**
 * Top utility bar — mirrors the AFB Hub's `.top-navigation-wrapper` strip.
 *
 * Left side: editing context (which supplier is loaded + signed-in user)
 * so the user always sees their grounding regardless of which wizard step
 * is open. Right side: Help (opens a support-request modal) + Sign Out.
 *
 * Sign Out hits the SAML logout endpoint once auth lands (build-order step
 * 4). During local-dev (Vite DEV mode) we intercept the click and explain
 * the bypass so the user doesn't 404 on a missing route.
 */
export default function TopNav() {
  // All hooks must run before any conditional return (Rules of Hooks) —
  // even though `useFormStore` results are unused on /sign-in, they have
  // to be subscribed to keep the hook order stable across renders.
  const location = useLocation();
  const hydrated = useFormStore((s) => s.hydrated);
  const supplier = useFormStore((s) => s.supplier);
  const originalSupplierId = useFormStore((s) => s.originalSupplier.id);

  const [whoami, setWhoami] = useState<WhoamiResponse | null>(null);
  useEffect(() => {
    api.get<WhoamiResponse>("/auth/whoami")
      .then(({ data }) => setWhoami(data))
      .catch(() => setWhoami(null));
  }, []);

  if (NO_TOP_NAV_PATHS.has(location.pathname)) return null;

  // An anonymous registration session has is_authenticated=false AND no
  // supplier loaded. LOCAL_DEV_BYPASS also has is_authenticated=false but
  // has a supplier_id from the bypass config, so we check both signals.
  // The editing-context strip and Sign Out are hidden for these so the
  // visitor isn't asked to "sign out" of a session they don't really have.
  const isAnonymousRegistration =
    whoami !== null && !whoami.is_authenticated && originalSupplierId == null;

  // SAML session → real name. Local-dev bypass → env-baked placeholder so
  // it's obvious you're not in production. Anonymous registration → blank
  // (the whole item is hidden below anyway).
  const signedInAs = isAnonymousRegistration
    ? ""
    : whoami?.display_name || import.meta.env.VITE_DEV_USER_NAME || "";
  const isNewSupplier = originalSupplierId == null;
  const supplierLabel =
    supplier.new_name || (isNewSupplier ? "New supplier registration" : "(unnamed supplier)");

  const onSignOut = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (import.meta.env.DEV) {
      e.preventDefault();
      Swal.fire({
        icon: "info",
        title: "Sign out unavailable in local dev",
        text: "LOCAL_DEV_BYPASS is enabled — there's no real session to end. SAML sign-out wires up in build-order step 4.",
        confirmButtonText: "OK",
      });
    }
    // In production the default <a href="/api/auth/saml/logout"> navigation runs.
  };

  const onHelp = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    // Anonymous registration sessions have no SAML identity — Supplier
    // Relations would receive a request with no return contact info.
    // Collect name/email/phone inline so they can actually reply.
    // Authenticated suppliers skip these (we already have name + email
    // from the SAML assertion + emailaddress from their Dataverse row).
    const askContact = isAnonymousRegistration;
    const contactFieldsHtml = askContact
      ? `
        <div class="mb-3">
          <label class="form-label" for="help-name">Name <span class="text-danger">*</span></label>
          <input id="help-name" class="form-control" type="text" maxlength="100"
            autocomplete="name" placeholder="First and last name" />
        </div>
        <div class="mb-3">
          <label class="form-label" for="help-email">Email <span class="text-danger">*</span></label>
          <input id="help-email" class="form-control" type="email" maxlength="254"
            autocomplete="email" placeholder="name@company.com" />
        </div>
        <div class="mb-3">
          <label class="form-label" for="help-phone">Phone</label>
          <input id="help-phone" class="form-control" type="tel" maxlength="14"
            inputmode="tel" autocomplete="tel" placeholder="(555) 555-5555" />
        </div>
      `
      : "";
    const result = await Swal.fire<{
      request_type: string;
      message: string;
      name?: string;
      email?: string;
      phone?: string;
    }>({
      title: "Contact Alliance Franchise Brands",
      width: 560,
      showCancelButton: true,
      confirmButtonText: "Send",
      cancelButtonText: "Cancel",
      focusConfirm: false,
      html: `
        <div class="text-start">
          ${contactFieldsHtml}
          <div class="mb-3">
            <label class="form-label" for="help-type">Request Type <span class="text-danger">*</span></label>
            <select id="help-type" class="form-select">
              <option value="question" selected>Question</option>
              <option value="issue">Issue / Bug</option>
            </select>
          </div>
          <div class="mb-1">
            <label class="form-label" for="help-message">Message <span class="text-danger">*</span></label>
            <textarea id="help-message" class="form-control" rows="6"
              placeholder="Describe your question or the issue you're seeing..."></textarea>
          </div>
        </div>
      `,
      didOpen: () => {
        // Live phone-number masking — mirrors the supplier wizard so the
        // value the team sees is always `(###) ###-####`. SweetAlert's
        // html: is a raw string so we wire the listener after render.
        const phoneEl = document.getElementById("help-phone") as HTMLInputElement | null;
        if (phoneEl) {
          phoneEl.addEventListener("input", () => {
            phoneEl.value = formatPhone(phoneEl.value);
          });
        }
      },
      preConfirm: () => {
        const typeEl = document.getElementById("help-type") as HTMLSelectElement;
        const msgEl = document.getElementById("help-message") as HTMLTextAreaElement;
        const message = msgEl.value.trim();

        let name: string | undefined;
        let email: string | undefined;
        let phone: string | undefined;
        if (askContact) {
          const nameEl = document.getElementById("help-name") as HTMLInputElement;
          const emailEl = document.getElementById("help-email") as HTMLInputElement;
          const phoneEl = document.getElementById("help-phone") as HTMLInputElement;
          name = nameEl.value.trim();
          email = emailEl.value.trim();
          phone = phoneEl.value.trim() || undefined;
          if (!name) {
            Swal.showValidationMessage("Please enter your name.");
            return false;
          }
          // Loose email shape check — defers final validation to the
          // backend / Mailgun. Catches obvious typos like missing @.
          if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            Swal.showValidationMessage("Please enter a valid email address.");
            return false;
          }
        }

        if (message.length < 5) {
          Swal.showValidationMessage("Please enter a message of at least 5 characters.");
          return false;
        }
        if (message.length > 2000) {
          Swal.showValidationMessage("Message is too long — please trim to 2000 characters.");
          return false;
        }
        return { request_type: typeEl.value, message, name, email, phone };
      },
    });

    if (!result.isConfirmed || !result.value) return;

    // Re-open as a loading state, fire the request, then morph into
    // success/error based on the outcome.
    Swal.fire({
      title: "Sending your request…",
      didOpen: () => Swal.showLoading(),
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
    });
    try {
      await api.post("/support/request", result.value);
      await Swal.fire({
        icon: "success",
        title: "Your request has been sent",
        text: "A member of the team will reach out as soon as possible.",
        confirmButtonText: "OK",
      });
    } catch {
      await Swal.fire({
        icon: "error",
        title: "Could not send your request",
        text: "Something went wrong on our end. Please try again in a moment.",
        confirmButtonText: "Close",
      });
    }
  };

  return (
    <div className="top-navigation-wrapper">
      <div className="container d-flex justify-content-between align-items-center flex-wrap gap-2 py-2">
        {/*
          Context strip on the left. Hidden until first prefill completes so
          we don't flash a misleading "New supplier" label while loading.
        */}
        <div className="top-nav-context">
          {hydrated && !isAnonymousRegistration && (
            <>
              <span className="ctx-item">
                <i className="fa-solid fa-building" aria-hidden="true" />
                <span className="ctx-label">Editing:</span>
                <span className="ctx-value">{supplierLabel}</span>
              </span>
              {signedInAs && (
                <>
                  <span className="ctx-divider" aria-hidden="true">·</span>
                  <span className="ctx-item">
                    <i className="fa-solid fa-user" aria-hidden="true" />
                    <span className="ctx-label">Signed in as:</span>
                    <span className="ctx-value">{signedInAs}</span>
                  </span>
                </>
              )}
            </>
          )}
        </div>

        <ul className="list-unstyled d-flex align-items-center gap-4 mb-0">
          <li>
            <a href="#" onClick={onHelp}>
              <i className="fa-solid fa-life-ring me-1" aria-hidden="true" />
              Help
            </a>
          </li>
          {!isAnonymousRegistration && (
            <li>
              <a href="/api/auth/saml/logout" onClick={onSignOut}>
                <i className="fa-solid fa-right-from-bracket me-1" aria-hidden="true" />
                Sign Out
              </a>
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}
